import { Controller } from '@hotwired/stimulus';
import { LOCAL_STOREAGE_KEY } from './consts';
/*
 * Description
 * -------
 *
 * Shows proper button based on post download status and handles its click.
 * Status comes from WLA event and is stored in local storage.
 *
 * Can be used on any element.
 * Can be used multiple times in the document.
 *
 * Targets
 * -------
 *
 * data                       - (required) - data container
 * download                   - (required) - download button
 * downloaded                 - (required) - downloaded button
 * downloadingWithoutProgress - (required) - downloading without progress button
 * downloadingWithProgress    - (required) - downloading with progress button
 * progress                   - (required) - progress bar
 *
 * Values
 * -------
 *
 * - id - (required) - post ID
 *
 * Usage
 * -------
 *
 * <div
 *   data-controller="wla-post-download"
 *   data-action="wlaDownloadsStatus@document->wla-post-download#handleEvent"
 *   data-wla-post-download-id-value="2"
 * >
 *   <div data-wla-post-download-target="data">{ ... }</div>
 *
 *   <button
 *     class="hidden"
 *     data-wla-post-download-target="download"
 *     data-action="click->wla-post-download#handleDownloadClick"
 *   >
 *     Download
 *   </button>
 *
 *   <button
 *     class="hidden"
 *     data-wla-post-download-target="downloaded"
 *     data-action="click->wla-post-download#handleDownloadedClick"
 *   >
 *     Downloaded
 *   </button>
 *
 *   <button
 *     class="hidden"
 *     data-wla-post-download-target="downloadingWithoutProgress"
 *     data-action="click->wla-post-download#handleDownloadingClick"
 *   >
 *     Downloading
 *   </button>
 *
 *   <button
 *     class="hidden"
 *     data-wla-post-download-target="downloadingWithProgress"
 *     data-action="click->wla-post-download#handleDownloadingClick"
 *   >
 *     <div data-wla-post-download-target="progress"></div>
 *     Downloading
 *   </button>
 * </div>
 */
export class WlaPostDownload extends Controller {
    static targets = [
        'data',
        'download',
        'downloaded',
        'downloadingWithoutProgress',
        'downloadingWithProgress',
        'progress',
    ];
    static values = { id: Number };
    connect() {
        const data = JSON.parse(localStorage.getItem(LOCAL_STOREAGE_KEY) || '[]');
        if (data)
            this.showStatus(data);
    }
    handleEvent(e) {
        localStorage.setItem(LOCAL_STOREAGE_KEY, JSON.stringify(e.detail));
        this.showStatus(e.detail);
    }
    showStatus(data) {
        this.downloadTarget.disabled = false;
        const postStatus = data.find((status) => status.postId === this.idValue);
        if (!postStatus)
            return this.showDownload();
        if (postStatus.status === 'downloaded')
            return this.showDownloaded();
        if (postStatus.status === 'downloading') {
            if (postStatus.progress === -1)
                return this.showDownloadingWithoutProgress();
            this.setProgress(postStatus.progress * 100);
            return this.showDownloadingWithProgress();
        }
    }
    handleDownloadClick() {
        window.webkit?.messageHandlers?.storeOffline?.postMessage?.(JSON.parse(this.mediaData));
        window.Android?.storeOffline?.(this.mediaData);
    }
    handleDownloadingClick() {
        window.webkit?.messageHandlers?.manageDownloadingOffline?.postMessage?.(JSON.parse(this.mediaData));
        window.Android?.handleDownloadingButtonClick?.(this.mediaData);
    }
    handleDownloadedClick() {
        window.webkit?.messageHandlers?.manageDownloadedOffline?.postMessage?.(JSON.parse(this.mediaData));
        window.Android?.handleDownloadedButtonClick?.(this.mediaData);
    }
    showDownload() {
        this.downloadTarget.classList.remove('hidden');
        this.downloadedTarget.classList.add('hidden');
        this.downloadingWithProgressTarget.classList.add('hidden');
        this.downloadingWithoutProgressTarget.classList.add('hidden');
    }
    showDownloaded() {
        this.downloadTarget.classList.add('hidden');
        this.downloadedTarget.classList.remove('hidden');
        this.downloadingWithProgressTarget.classList.add('hidden');
        this.downloadingWithoutProgressTarget.classList.add('hidden');
    }
    showDownloadingWithProgress() {
        this.downloadTarget.classList.add('hidden');
        this.downloadedTarget.classList.add('hidden');
        this.downloadingWithProgressTarget.classList.remove('hidden');
        this.downloadingWithoutProgressTarget.classList.add('hidden');
    }
    showDownloadingWithoutProgress() {
        this.downloadTarget.classList.add('hidden');
        this.downloadedTarget.classList.add('hidden');
        this.downloadingWithProgressTarget.classList.add('hidden');
        this.downloadingWithoutProgressTarget.classList.remove('hidden');
    }
    setProgress(progress) {
        this.progressTarget.style.setProperty('--progress', progress + '%');
    }
    get mediaData() {
        return this.dataTarget.innerHTML;
    }
}
